import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, X, Download, Eye, PlusCircle, CopyPlus } from "lucide-react";
import { api } from "../lib/api";

export const WaitlistNudgeBanner = ({ nudge, eventId, variant = "compact", onDismissed, onViewWaitlist }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (!nudge || hidden) return null;

  const dismiss = async (action) => {
    setMenuOpen(false);
    setHidden(true);
    try {
      await api.post(`/admin/events/${eventId}/waitlist-nudge/dismiss`, { action });
    } catch { /* advisory only — ignore */ }
    onDismissed?.(action);
  };

  return (
    <div
      className="border border-amber-500 bg-amber-50 rounded-sm p-3 text-sm relative"
      data-testid={`waitlist-nudge-${variant}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-2 pr-8">
        <Flame size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold" data-testid="waitlist-nudge-message">{nudge.message}</span>
          {(nudge.sections || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="waitlist-nudge-sections">
              {nudge.sections.map((s) => (
                <span key={s.section} className="text-xs border border-amber-400 rounded-sm px-1.5 py-0.5 bg-white">
                  {s.section}: {s.waiting} waiting ({s.requested_tickets} tickets), sold out
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold">
            {variant === "full" ? (
              <>
                <Link to={`/dashboard/events/${eventId}/edit`} className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="nudge-add-capacity-link">
                  <PlusCircle size={12} /> Add capacity
                </Link>
                <button type="button" onClick={async () => {
                  const dup = await api.post(`/events/${eventId}/second-show?from_waitlist_nudge=true`);
                  window.location.href = `/dashboard/events/${dup.data.id}/edit`;
                }} className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="nudge-duplicate-link">
                  <CopyPlus size={12} /> Duplicate as second show
                </button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/waitlist/export`} className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="nudge-export-csv-link">
                  <Download size={12} /> Export waitlist CSV
                </a>
                <button type="button" onClick={onViewWaitlist} className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="nudge-view-waitlist-btn">
                  <Eye size={12} /> View waitlist
                </button>
              </>
            ) : (
              <Link to={`/dashboard/events/${eventId}/reports`} className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="nudge-view-waitlist-link">
                <Eye size={12} /> View waitlist
              </Link>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        className="absolute top-2 right-2 text-amber-700 hover:text-amber-900"
        aria-label="Dismiss nudge"
        data-testid="waitlist-nudge-dismiss-btn"
      >
        <X size={15} />
      </button>
      {menuOpen && (
        <div className="absolute top-8 right-2 z-20 bg-white border border-[var(--gt-line)] rounded-sm shadow-md text-xs" data-testid="waitlist-nudge-dismiss-menu">
          {[["snooze24", "Snooze 24 hours"], ["snooze7d", "Snooze 7 days"], ["dismiss", "Dismiss for this event"]].map(([action, label]) => (
            <button key={action} type="button" onClick={() => dismiss(action)}
              className="block w-full text-left px-3 py-2 hover:bg-[var(--gt-bg-surface)]"
              data-testid={`nudge-${action}-btn`}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default WaitlistNudgeBanner;
